import React, { useMemo, useState } from 'react';

export interface DefectBarDatum {
  /** Stable key — colour/identity follows the entity, never its rank. */
  id: string;
  label: string;
  /** Optional short code shown before the label. */
  code?: string;
  value: number;
}

interface DefectBarListProps {
  data: DefectBarDatum[];
  /** Rows with a zero value are hidden behind a toggle when true. */
  collapseZeros?: boolean;
  emptyLabel?: string;
  unit?: string;
}

/**
 * Ranked horizontal bar list — one series, so no legend is needed (the card
 * title names it). Bars are scaled against the largest value in the set, not
 * an arbitrary multiplier, so length is proportional to magnitude.
 */
export function DefectBarList({
  data,
  collapseZeros = true,
  emptyLabel = 'No defects recorded in this period.',
  unit = 'defects',
}: DefectBarListProps) {
  const [showAll, setShowAll] = useState(false);
  const [active, setActive] = useState<string | null>(null);
  const [asTable, setAsTable] = useState(false);

  const sorted = useMemo(() => [...data].sort((a, b) => b.value - a.value), [data]);

  const max = sorted.length ? sorted[0].value : 0;
  const total = useMemo(() => sorted.reduce((sum, d) => sum + d.value, 0), [sorted]);

  const zeroCount = sorted.filter((d) => d.value === 0).length;
  const visible = collapseZeros && !showAll ? sorted.filter((d) => d.value > 0) : sorted;

  if (total === 0) {
    return (
      <p className="py-6 text-center text-[0.8125rem] text-[var(--text-dim)]">{emptyLabel}</p>
    );
  }

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <button
          type="button"
          onClick={() => setAsTable((v) => !v)}
          className="text-[0.75rem] font-medium text-[var(--text-muted)] underline-offset-2 hover:text-[var(--text-main)] hover:underline"
        >
          {asTable ? 'View chart' : 'View as table'}
        </button>
      </div>

      {asTable ? (
        /* Table-view twin — the WCAG-clean equivalent of the chart above. */
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Component</th>
                <th className="text-right">{unit === 'defects' ? 'Defects' : unit}</th>
                <th className="text-right">Share</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((d) => (
                <tr key={d.id}>
                  <td>
                    {d.code && <span className="mr-1.5 text-[var(--text-dim)]">{d.code}</span>}
                    {d.label}
                  </td>
                  <td className="text-right tnum">{d.value}</td>
                  <td className="text-right tnum text-[var(--text-muted)]">
                    {total ? Math.round((d.value / total) * 100) : 0}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {visible.map((d) => {
            const pctOfMax = max ? (d.value / max) * 100 : 0;
            const share = total ? Math.round((d.value / total) * 100) : 0;
            const isActive = active === d.id;

            return (
              <li
                key={d.id}
                tabIndex={0}
                onMouseEnter={() => setActive(d.id)}
                onMouseLeave={() => setActive(null)}
                onFocus={() => setActive(d.id)}
                onBlur={() => setActive(null)}
                className="relative grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 rounded-[var(--radius-sm)] outline-none sm:grid-cols-[11rem_minmax(0,1fr)_2.5rem]"
              >
                <div className="flex min-w-0 items-baseline gap-1.5 text-[0.8125rem]">
                  {d.code && (
                    <span className="flex-shrink-0 text-[0.75rem] text-[var(--text-dim)]">{d.code}</span>
                  )}
                  <span className="truncate text-[var(--text-main)]">{d.label}</span>
                </div>

                <span className="justify-self-end text-[0.8125rem] tnum text-[var(--text-muted)] sm:order-last">
                  {d.value}
                </span>

                {/* Track + bar. Thin mark, rounded data-end, anchored at the baseline. */}
                <div className="col-span-2 h-2 w-full overflow-hidden rounded-full bg-[var(--viz-track)] sm:col-span-1 sm:order-2">
                  <div
                    className="h-full rounded-full transition-[width,opacity] duration-300"
                    style={{
                      width: `${Math.max(pctOfMax, d.value > 0 ? 2 : 0)}%`,
                      background: 'var(--viz-series-1)',
                      opacity: isActive ? 1 : 0.85,
                    }}
                  />
                </div>

                {isActive && (
                  <div
                    role="tooltip"
                    className="pointer-events-none absolute -top-1 right-0 z-10 -translate-y-full whitespace-nowrap rounded-[var(--radius-sm)] border border-[var(--card-border)] bg-[var(--bg-secondary)] px-2 py-1 text-[0.75rem] text-[var(--text-main)] shadow-[var(--shadow-md)]"
                  >
                    {d.label}: <span className="tnum font-medium">{d.value}</span> {unit}
                    <span className="text-[var(--text-dim)]"> · {share}% of total</span>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!asTable && collapseZeros && zeroCount > 0 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-3 text-[0.75rem] font-medium text-[var(--accent-color)] underline-offset-2 hover:underline"
        >
          {showAll
            ? `Hide ${zeroCount} with no defects`
            : `Show ${zeroCount} more with no defects`}
        </button>
      )}
    </div>
  );
}
